import { useMemo } from 'react'

import { useParams } from 'react-router-dom'

import { useGetManualMatchingCuratorsQuery } from 'src/Redux/manualMatchingCuratorsApi'
import { useGetSpotifyCampaignStatusQuery } from 'src/Redux/spotifyCampaignStatusApi'
import {
  CampaignStatusEnum,
  SpotifyCampaignMatchingType,
} from 'src/Types/campaigns'
import { useSpotifyCampaignReviews } from 'src/Redux/artistSpotifyCampaignReviewApi/useSpotifyCampaignReviews'
import { transformPlaylistApiUrlToBrowserUrl } from 'src/Pages/Curator/PlaylistsPage/components/PlayList/halpers'

export type ReviewTypeStatus =
  | 'placement'
  | 'feedback'
  | 'awaiting'
  | 'notReviewed'

export interface CampaignReview {
  id: number
  curatorFirstName: string
  curatorLastName: string
  followersCount: number
  playlistsCount: number
  userCover: string
  status?: ReviewTypeStatus
  comment?: string
  matchedTrackPlaylists?: Array<{ name: string | null; url: string | null }>
  addedTrackPlaylist?: {
    playlistName: string
    playlistUrl: string
    playlistCoverUrl: string
  } | null
  reviewComplain?: boolean
  vocalRating?: number
  melodyRating?: number
  productionRating?: number
  curatorUserId?: number
  isFavorite?: boolean
  reviewDate?: string
}

interface UseCampaignDetailsReviewsReturn {
  reviews: CampaignReview[] | undefined
  isLoading: boolean
  isError: boolean
  isManualMatching: boolean
}

export const useCampaignDetailsReviews =
  (): UseCampaignDetailsReviewsReturn => {
    const { campaignID } = useParams<{ campaignID: string }>()

    const { data: campaignStatus, isLoading: isStatusLoading } =
      useGetSpotifyCampaignStatusQuery(Number(campaignID), {
        skip: !campaignID,
      })

    const isManualMatching =
      campaignStatus?.matchingType === SpotifyCampaignMatchingType.MANUAL

    const {
      data: manualMatchingCurators = [],
      isLoading: isCuratorsLoading,
      isError: isCuratorsError,
    } = useGetManualMatchingCuratorsQuery(
      { campaignId: campaignID || '' },
      { skip: !campaignID || !isManualMatching },
    )

    const { reviews } = useSpotifyCampaignReviews({
      status: campaignStatus?.status,
    })

    const isLoading = isStatusLoading || (isManualMatching && isCuratorsLoading)
    const isError = isManualMatching ? isCuratorsError : false

    const isCampaignFinished =
      campaignStatus?.status === CampaignStatusEnum.ENDED ||
      campaignStatus?.status === CampaignStatusEnum.STOPPED

    const manualMatchingReviews: CampaignReview[] = manualMatchingCurators.map(
      (curator) => ({
        curatorFirstName: curator.firstName,
        curatorLastName: curator.lastName,
        followersCount: curator.followersCount,
        playlistsCount: curator.playlistsCount,
        userCover: curator.userPhotoUrl ?? '',
        id: curator.userId,
        status: isCampaignFinished ? 'notReviewed' : 'awaiting',
        userId: curator.userId,
      }),
    )

    const automatedReviews: CampaignReview[] | undefined = reviews?.map(
      ({ review, curator, matchedPlaylists, addedToPlaylist }) => {
        const addedTrackPlaylist = addedToPlaylist
          ? {
              playlistName: addedToPlaylist.name || '',
              playlistUrl: transformPlaylistApiUrlToBrowserUrl(
                addedToPlaylist.url || '',
              ),
              playlistCoverUrl: addedToPlaylist.cover,
            }
          : null

        const reviewedStatus = addedTrackPlaylist ? 'placement' : 'feedback'
        const isSkipped = review.reviewStatus === 'skipped'

        return {
          id: review.id,
          comment: review.comment,
          curatorFirstName: curator.firstName,
          curatorLastName: curator.lastName,
          followersCount: curator.followersCount,
          playlistsCount: curator.playlistsCount,
          userCover: curator.userPhotoUrl ?? '',
          matchedTrackPlaylists: matchedPlaylists,
          reviewComplain: review.artistFeedbackStatus,
          vocalRating: review.vocalRating,
          melodyRating: review.melodyRating,
          productionRating: review.productionRating,
          curatorUserId: curator.userId,
          isFavorite: Boolean(curator.isFavorite),
          status: isSkipped ? 'notReviewed' : reviewedStatus,
          reviewDate: review.reviewDate,
          addedTrackPlaylist,
        }
      },
    )

    const mergedManualMatchingReviews: CampaignReview[] = useMemo(() => {
      const reviewedUserIds = new Set(
        automatedReviews?.map((r) => r.curatorUserId) ?? [],
      )

      const filteredManualReviews = manualMatchingReviews.filter(
        (r) => !reviewedUserIds.has(r.id),
      )

      return [...filteredManualReviews, ...(automatedReviews ?? [])]
    }, [manualMatchingReviews, automatedReviews])

    return useMemo(
      () => ({
        reviews: isManualMatching
          ? mergedManualMatchingReviews
          : automatedReviews,
        isLoading,
        isError,
        isManualMatching,
      }),
      [
        isManualMatching,
        mergedManualMatchingReviews,
        automatedReviews,
        isLoading,
        isError,
      ],
    )
  }
